import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  propertyTypes,
  budgetRanges,
  bedroomOptions,
  faroLocations,
  mainFactors,
} from '../mockData';
import { submitDetailedLead } from '../api';
import { toast } from 'sonner';
import { MessageCircle } from 'lucide-react';

export const DetailedForm = () => {
  const [intent, setIntent] = useState('buy');
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '',
    intent: 'buy',
    propertyType: '', budget: '', bedrooms: '',
    location: '', mainFactor: '', additionalInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleIntentChange = (value) => {
    setIntent(value);
    setFormData(prev => ({ ...prev, intent: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const result = await submitDetailedLead(formData);
      setSuccess({ whatsapp_url: result.whatsapp_url });
      toast.success("Briefing enviado. Obrigado!");
      setFormData({
        name: '', email: '', phone: '',
        intent: intent,
        propertyType: '', budget: '', bedrooms: '',
        location: '', mainFactor: '', additionalInfo: ''
      });
    } catch (error) {
      toast.error('Erro ao enviar. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "mt-3 border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus:border-stone-900 bg-transparent";
  const selectClass = "mt-3 border-0 border-b border-stone-300 rounded-none px-0 focus:ring-0 bg-transparent shadow-none";
  const labelClass = "text-xs tracking-wider uppercase text-stone-500 font-medium";

  return (
    <section id="detailed-form" className="py-20 md:py-32 bg-stone-50" data-testid="detailed-form-section">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-16">
            <span className="text-xs tracking-[0.2em] uppercase text-stone-500 font-medium" data-testid="detailed-form-label">
              Briefing detalhado
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-stone-900 mt-4 leading-tight italic font-serif uppercase tracking-tight" data-testid="detailed-form-title">
              O que procura?
            </h2>
            <p className="text-stone-600 font-light mt-6 max-w-xl italic">
              Quanto mais detalhes me der, mais personalizada será a proposta.
            </p>
          </div>

          {success && (
            <div className="text-center space-y-6 py-12 border-t border-b border-stone-200" data-testid="detailed-success">
              <p className="text-xs tracking-[0.2em] uppercase text-stone-500 font-medium">Recebido</p>
              <h3 className="text-3xl font-light text-stone-900">Briefing recebido.</h3>
              <p className="text-stone-600 font-light max-w-md mx-auto">
                Vou analisar com atenção e contactá-lo. Se preferir, podemos continuar a conversa já no WhatsApp.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-4">
                <a
                  href={success.whatsapp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-button-detailed"
                  className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe57] text-white px-6 py-4 text-sm font-medium tracking-wide rounded-none transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  Continuar no WhatsApp
                </a>
                <button
                  onClick={() => setSuccess(null)}
                  className="text-sm text-stone-500 hover:text-stone-900 underline-offset-4 hover:underline transition-colors"
                  data-testid="detailed-send-another"
                >
                  Enviar outro briefing
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className={`space-y-10 ${success ? 'hidden' : ''}`} data-testid="detailed-form">
            {/* Intent Toggle - minimal */}
            <div>
              <Label className={labelClass}>Pretende</Label>
              <div className="grid grid-cols-2 mt-4 border border-stone-300">
                <button
                  type="button"
                  onClick={() => handleIntentChange('buy')}
                  data-testid="intent-buy"
                  className={`py-4 text-sm font-medium tracking-wide transition-colors ${
                    intent === 'buy' ? 'bg-stone-900 text-white' : 'bg-transparent text-stone-700 hover:bg-stone-100'
                  }`}
                >
                  Comprar
                </button>
                <button
                  type="button"
                  onClick={() => handleIntentChange('sell')}
                  data-testid="intent-sell"
                  className={`py-4 text-sm font-medium tracking-wide transition-colors ${
                    intent === 'sell' ? 'bg-stone-900 text-white' : 'bg-transparent text-stone-700 hover:bg-stone-100'
                  }`}
                >
                  Vender
                </button>
              </div>
            </div>

            {/* Personal Info */}
            <div className="space-y-6">
              <h3 className="text-xs tracking-[0.2em] uppercase text-stone-500 font-medium border-t border-stone-200 pt-8">
                01 · Contacto
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <Label htmlFor="detailed-name" className={labelClass}>Nome</Label>
                  <Input id="detailed-name" name="name" value={formData.name} onChange={handleChange} required data-testid="detailed-name-input" className={inputClass} />
                </div>
                <div>
                  <Label htmlFor="detailed-phone" className={labelClass}>Telefone</Label>
                  <Input id="detailed-phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} required data-testid="detailed-phone-input" className={inputClass} />
                </div>
              </div>
              <div>
                <Label htmlFor="detailed-email" className={labelClass}>Email</Label>
                <Input id="detailed-email" name="email" type="email" value={formData.email} onChange={handleChange} required data-testid="detailed-email-input" className={inputClass} />
              </div>
            </div>

            {/* Property Details */}
            <div className="space-y-6">
              <h3 className="text-xs tracking-[0.2em] uppercase text-stone-500 font-medium border-t border-stone-200 pt-8">
                02 · Imóvel
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <Label className={labelClass}>Tipo de Imóvel</Label>
                  <Select value={formData.propertyType} onValueChange={(v) => handleSelectChange('propertyType', v)} required>
                    <SelectTrigger data-testid="detailed-property-type" className={selectClass}>
                      <SelectValue placeholder="Selecionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={labelClass}>Número de Quartos</Label>
                  <Select value={formData.bedrooms} onValueChange={(v) => handleSelectChange('bedrooms', v)}>
                    <SelectTrigger data-testid="detailed-bedrooms" className={selectClass}>
                      <SelectValue placeholder="Selecionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {bedroomOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={labelClass}>Localização</Label>
                  <Select value={formData.location} onValueChange={(v) => handleSelectChange('location', v)} required>
                    <SelectTrigger data-testid="detailed-location" className={selectClass}>
                      <SelectValue placeholder="Portimão / Algarve" />
                    </SelectTrigger>
                    <SelectContent>
                      {faroLocations.map((l) => (
                        <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={labelClass}>{intent === 'buy' ? 'Orçamento' : 'Preço Pretendido'}</Label>
                  <Select value={formData.budget} onValueChange={(v) => handleSelectChange('budget', v)}>
                    <SelectTrigger data-testid="detailed-budget" className={selectClass}>
                      <SelectValue placeholder="Selecionar faixa" />
                    </SelectTrigger>
                    <SelectContent>
                      {budgetRanges.map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className={labelClass}>
                  Fator mais importante na {intent === 'buy' ? 'compra' : 'venda'}
                </Label>
                <Select value={formData.mainFactor} onValueChange={(v) => handleSelectChange('mainFactor', v)} required>
                  <SelectTrigger data-testid="detailed-main-factor" className={selectClass}>
                    <SelectValue placeholder="O que mais valoriza?" />
                  </SelectTrigger>
                  <SelectContent>
                    {mainFactors.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Additional */}
            <div className="space-y-6">
              <h3 className="text-xs tracking-[0.2em] uppercase text-stone-500 font-medium border-t border-stone-200 pt-8">
                03 · Notas
              </h3>
              <div>
                <Label htmlFor="additional-info" className={labelClass}>Informações adicionais</Label>
                <Textarea
                  id="additional-info"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Conte-me mais..."
                  data-testid="detailed-additional-info"
                  className={`${inputClass} placeholder:text-stone-400`}
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="detailed-submit-button"
              className="w-full bg-stone-900 hover:bg-stone-800 text-white font-medium py-6 text-sm tracking-wide rounded-none mt-8 min-h-[52px]"
            >
              {isSubmitting ? 'A enviar...' : 'Enviar briefing'}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};
